import { useCmsContents } from '@bluheadless/ui-logic/src/hooks/cms/useCmsContents'
import { MenuItem } from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { cx } from '@emotion/css'
import router from 'next/router'
import { string } from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { InputAutocomplete, Root } from './customer-care-search.styled'

const CustomerCareSearch = ({ className, autocompleteProps, items }) => {
	const { contents } = useCmsContents({ type: 'cc_search' })
	const { formatMessage } = useIntl()
	const [searchValue, setSearchValue] = useState(null)
	const [inputValue, setInputValue] = useState('')

	/**
	 * Enables filtering options by tags:
	 * if user types 'order' in the search input and there isn't any label
	 * that contains that word, labels that contain 'order' in the tags will be listed.
	 */
	const filterOptions = createFilterOptions({
		stringify: (option) => option.tags,
	})

	useEffect(() => {
		if (searchValue?.value) {
			router.push({ pathname: searchValue?.value })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue])

	const mergedItems = items || []
	const mergedContents = contents ? [...contents, ...mergedItems] : mergedItems

	return mergedContents?.length ? (
		<Root className={cx('CustomerCareSearch-root', className)}>
			<InputAutocomplete
				autocompleteProps={{
					autoSelect: false,
					freeSolo: false,
					popupIcon: null, // disable popup icon generated with !freeSolo
					blurOnSelect: true,
					getOptionLabel: (option) => option.label,
					isOptionEqualToValue: (option, value) => (option?.label ?? option) === (value?.label ?? value),
					options: mergedContents.map(
						({ metadata: { cc_search_page_url_key, cc_search_title, cc_search_tags } = {} }) => ({
							value: cc_search_page_url_key,
							label: cc_search_title,
							tags: cc_search_tags || '',
						})
					),
					autoComplete: true,
					filterSelectedOptions: true,
					filterOptions: filterOptions,
					onChange: (e, value) => setSearchValue(value),
					onInputChange: (e, newInputValue) => setInputValue(newInputValue),
					value: searchValue,
					name: 'cc_search',
					placeholder: formatMessage({ id: 'search' }),
					// eslint-disable-next-line react/display-name
					renderOption: (props, { label }) => {
						if (label) {
							const matches = match(label, inputValue, { insideWords: true, findAllOccurrences: true })
							const parts = parse(label, matches)

							return (
								parts && (
									<MenuItem {...props}>
										{parts.map((part, index) => (
											<span key={index} style={{ fontWeight: part.highlight ? 700 : 400, whiteSpace: 'break-spaces' }}>
												{part.text}
											</span>
										))}
									</MenuItem>
								)
							)
						}
					},
					disablePortal: true,
					...autocompleteProps,
				}}
			/>
		</Root>
	) : null
}

CustomerCareSearch.defaultProps = {
	...InputAutocomplete.defaultProps,
	noOptionsText: <FormattedMessage id="cc_search_no_results" />,
}
CustomerCareSearch.propTypes = {
	...InputAutocomplete.propTypes,
	/**
	 * Class name for the root element.
	 */
	className: string,
}

export default CustomerCareSearch
